import { Box, Button, Typography } from "@mui/material";
import ExportButton from "components/button/ExportButton";
import React, { ChangeEvent, useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import useLoadMore from "hooks/useLoadMore";
import { EstQuoteDetailTire, ITireItemDetail, PrcFeesData } from "features/car_model/types";
import Notify from "config/notify";
import routes from "constants/routes";
import { SESSION_KEY } from "constants/key";
import { CostPriceMap, MstProcFeeTireItem } from "features/mst_proc_fee/types/proc-fee";
import FilterProcFeeModal from "features/mst_proc_fee/components/FilterProcFeeModal";
import TireInfoSection from "features/mst_proc_fee/components/TireInfoSection";
import ProcFeeTable from "features/mst_proc_fee/components/ProcFeeTable";
import useSetTireProcFeeList from "features/mst_proc_fee/hooks/useSetTireProcFeeList";
import { useTirePriceCalculator } from "features/mst_proc_fee/hooks/useTirePriceCalculator";
import TireListTable from "features/mst_proc_fee/components/TireListTable";
import {
  DEFAULT_TIRE_NUMBER,
  MAX_TIRE_SELECTION,
  TIRE_TYPE_OPTIONS,
} from "features/mst_proc_fee/constants/options";
import { fetchCostPricesForTires } from "features/mst_proc_fee/api/procFeeApi";

const IPAD_LANDSCAPE_WIDTH_MIN = 1024;
const IPAD_LANDSCAPE_WIDTH_MAX = 1280;
const ZOOM_OUT_SCALE = 0.75;

/**
 * Main component for setting tire procedure fees.
 * When viewport width is 1024–1366px (e.g. iPad landscape), page zooms out to 75%.
 */
function SetTireProcFeeList() {
  // ========================================================================
  // Hooks & State
  // ========================================================================
  const navigate = useNavigate();
  const [zoomOutForIpad, setZoomOutForIpad] = useState(false);

  const {
    procFees,
    setProcFees,
    tireList,
    makerList,
    categoryList,
    selectedCategory,
    setSelectedCategory,
    setTireList,
    loading,
    onLoadMore,
    handleFilter,
    selectedTireIds,
    setSelectedTireIds,
    selectedMaker,
    setSelectedMaker,
    mstProcFee,
    page,
    totalSelectedTire,
    setTotalSelectedTire,
    recommendFlag,
    setRecommendFlag,
    punctureProtection,
    tireCategory,
    priceSettingWheel,
  } = useSetTireProcFeeList();

  const tireNumber= DEFAULT_TIRE_NUMBER;

  // const [tireNumber, setTireNumber] = useState(
  //   (Array.isArray(mstProcFee?.tbl_quote_detail_tire) &&
  //     mstProcFee.tbl_quote_detail_tire[0]?.quantity) ||
  //     DEFAULT_TIRE_NUMBER,
  // );

  const { renderNodeLoader } = useLoadMore(onLoadMore, loading);

  const [errors, setErrors] = useState<{ [key: string]: boolean }>({});
  const [punctureCompensation, setPunctureCompensation] = useState(
    mstProcFee?.cashback?.puncture || 0,
  );
  const [cashbackPeriod, setCashbackPeriod] = useState(mstProcFee?.cashback?.cashback_period || 1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [stockFilterStore, setStockFilterStore] = useState(false);
  const [stockFilterDepotVendor, setStockFilterDepotVendor] = useState(false);

  // Checkbox states for procedure fees
  const [isDisposalChecked, setIsDisposalChecked] = useState(
    mstProcFee?.proc_fees?.waste_tire_cost?.checked === undefined
      ? true
      : mstProcFee?.proc_fees?.waste_tire_cost?.checked,
  );
  const [isValveChecked, setIsValveChecked] = useState(
    mstProcFee?.proc_fees?.rubber_stem_valve_cost?.checked === undefined
      ? true
      : mstProcFee?.proc_fees?.rubber_stem_valve_cost?.checked,
  );
  const [isNitrogenChecked, setIsNitrogenChecked] = useState(
    mstProcFee?.proc_fees?.nitrogen_inflation_cost?.checked === undefined
      ? false
      : mstProcFee?.proc_fees?.nitrogen_inflation_cost?.checked,
  );
  const [isHubProtectionChecked, setIsHubProtectionChecked] = useState(
    mstProcFee?.proc_fees?.hub_rust_prevention_cost?.checked === undefined
      ? false
      : mstProcFee?.proc_fees?.hub_rust_prevention_cost?.checked,
  );

  // ========================================================================
  // Session Storage Data
  // ========================================================================
  const prevSite = sessionStorage.getItem(SESSION_KEY.NEW_QUOTE_FROM) || "";
  const maker = JSON.parse(sessionStorage.getItem(SESSION_KEY.NEW_QUOTE_MAKER) || "{}");
  const carTireListByCode: ITireItemDetail[] = JSON.parse(
    sessionStorage.getItem(SESSION_KEY.NEW_QUOTE_TIRE) || "{}",
  );

  // ========================================================================
  // Cost Price Map for special pricing
  // ========================================================================
  const [costPriceMap, setCostPriceMap] = useState<CostPriceMap>({});

  // Fetch cost prices only when the response-level price_setting_wheel flag is 1.
  useEffect(() => {
    const loadCostPrices = async () => {
      const store_cd = localStorage.getItem("shop_cd");
      if (priceSettingWheel === 1 && tireList.length > 0 && store_cd) {
        const map = await fetchCostPricesForTires(tireList, store_cd);
        setCostPriceMap(map);
      } else {
        setCostPriceMap({});
      }
    };

    loadCostPrices();
  }, [tireList.length, priceSettingWheel]);

  // ========================================================================
  // Calculator Hook
  // ========================================================================
  const { calculator } = useTirePriceCalculator({
    tireNumber,
    procFees,
    isDisposalChecked,
    isValveChecked,
    isNitrogenChecked,
    isHubProtectionChecked,
    punctureCompensation,
    cashbackPeriod,
    punctureProtection,
    costPriceMap,
  });

  // ========================================================================
  // Memoized Values
  // ========================================================================
  const tireCategoryText = useMemo<string>(
    () => TIRE_TYPE_OPTIONS.find((item) => item.value === tireCategory)?.label || "",
    [tireCategory],
  );

  const renderSelectedCar = useMemo(() => {
    if (Object.keys(maker).length === 0) return "";
    const { car_model_and_period, modl_name } = maker;
    if (!car_model_and_period || !modl_name) return "";

    if (car_model_and_period.includes(modl_name)) {
      const periodPart = car_model_and_period.replace(modl_name, "").trim();
      return (
        <>
          {modl_name} <br />
          {periodPart}
        </>
      );
    }
    return car_model_and_period;
  }, [maker]);

  // ========================================================================
  // Event Handlers
  // ========================================================================
  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const handleFilterClick = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const handleDisposalChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setIsDisposalChecked(event.target.checked);
  }, []);

  const handleValveChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setIsValveChecked(event.target.checked);
  }, []);

  const handleNitrogenChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setIsNitrogenChecked(event.target.checked);
  }, []);

  const handleHubProtectionChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setIsHubProtectionChecked(event.target.checked);
  }, []);

  // tireId = String(tire.id): stable unique key that survives pagination.
  // Not array index (shifts when pages are appended) or item_cd (may duplicate).
  const onSelectTire = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>, tireId: string) => {
      if (totalSelectedTire >= MAX_TIRE_SELECTION && event.target.checked) {
        Notify.setNotification({
          show: true,
          title: `${MAX_TIRE_SELECTION}個までしか選択できません`,
          actionsPosition: "end",
        });
        return;
      }

      if (event.target.checked) {
        setTotalSelectedTire(totalSelectedTire + 1);
      } else {
        setTotalSelectedTire(totalSelectedTire - 1);
      }

      setSelectedTireIds({
        ...selectedTireIds,
        [tireId]: event.target.checked,
      });
    },
    [totalSelectedTire, setTotalSelectedTire, selectedTireIds, setSelectedTireIds],
  );

  const onChangeInput = useCallback(
    (e: ChangeEvent<HTMLInputElement>, fieldName: keyof MstProcFeeTireItem) => {
      const numericValue = e.target.value.replace(/[^0-9]/g, "");
      setProcFees({
        ...procFees,
        [fieldName]: Number(numericValue),
      } as MstProcFeeTireItem);
    },
    [procFees, setProcFees],
  );

  const handleErrorChange = useCallback((field: string, error: boolean) => {
    setErrors((prev) => ({ ...prev, [field]: error }));
  }, []);

  const navigateConfirmResult = useCallback(() => {
    if (Object.values(selectedTireIds).every((item) => item === false)) {
      Notify.setNotification({
        show: true,
        title: "商品を選択してください",
        actionsPosition: "center",
      });
      return;
    }
    if (Object.values(errors).some((item) => item)) return;

    const sessionData: Record<string, any> = {};

    if (procFees) {
      sessionData.proc_fees = Object.keys(procFees).reduce((acc, key) => {
        switch (key) {
          case "replac_labor_cost":
            acc[key] = {
              value: procFees[key],
              checked: true,
            };
            break;
          case "waste_tire_cost":
            acc[key] = {
              value: procFees[key],
              checked: isDisposalChecked,
            };
            break;
          case "rubber_stem_valve_cost":
            acc[key] = {
              value: procFees[key],
              checked: isValveChecked,
            };
            break;
          case "nitrogen_inflation_cost":
            acc[key] = {
              value: procFees[key],
              checked: isNitrogenChecked,
            };
            break;
          case "hub_rust_prevention_cost":
            acc[key] = {
              value: procFees[key],
              checked: isHubProtectionChecked,
            };
            break;
          default:
            break;
        }
        return acc;
      }, {} as PrcFeesData);
    }

    const detail: Partial<EstQuoteDetailTire>[] = [];
    tireList.forEach((tire) => {
      // Look up by String(tire.id) — matches the key set in onSelectTire / formatTireList
      if (!selectedTireIds[String(tire.id)]) return;

      const result = calculator(tire);
      const {
        basePrice,
        discountedPrice,
        wage,
        totalPrice,
        compensation,
        totalPriceWithCompensation,
      } = result;

      detail.push({
        checked: true,
        tire_item_cd: tire.item_cd,
        maker_name: tire.maker_name,
        item_name: tire.item_name,
        tire_loin_speed_mark: tire.tire_loin_speed_mark,
        tire_label: tire.tire_label,
        quantity: tireNumber,
        nomal_price: basePrice,
        fixed_discount: Number(tire.fixed_discount),
        special_discount: Number(tire.special_discount),
        discounted_sale_price: discountedPrice,
        wage_cost_price: wage,
        wage_cost_in_price: totalPrice,
        puncture_protection_price: compensation,
        puncture_protection_in_price: totalPriceWithCompensation,
        cb_tire_price: tire.cb_tire_price,
        cb_wheel_price: tire.cb_wheel_price,
        confirm_total_price: totalPriceWithCompensation,
        remarks: "",
      });
    });

    sessionData.tbl_quote_detail_tire = detail;
    sessionData.cashback = {
      puncture: punctureCompensation,
      cashback_period: cashbackPeriod,
    };

    sessionStorage.setItem(SESSION_KEY.SELECTED_TIRE, JSON.stringify(sessionData));
    sessionStorage.setItem(SESSION_KEY.SET_TIRE_RECOMMEND_FLAG, JSON.stringify(recommendFlag));
    sessionStorage.setItem(SESSION_KEY.SET_TIRE_CURRENT_PAGE, String(page));
    sessionStorage.setItem(SESSION_KEY.TIRE_WHEEL_TIRE_FROM, routes.mstSelectedTireProcFee);

    // TODO: Clear wheel store state since this is a fresh selection flow
    sessionStorage.removeItem(SESSION_KEY.WHEEL_STORE_STATE);

    navigate(routes.mstTireWheelStore);
  }, [
    selectedTireIds,
    procFees,
    isDisposalChecked,
    isValveChecked,
    isNitrogenChecked,
    isHubProtectionChecked,
    tireList,
    calculator,
    tireNumber,
    punctureCompensation,
    cashbackPeriod,
    recommendFlag,
    page,
    navigate,
  ]);

  // ========================================================================
  // Effects
  // ========================================================================
  useEffect(() => {
    if ([1, 2].includes(tireNumber)) {
      setPunctureCompensation(0);
      setCashbackPeriod(1);
    }
  }, [tireNumber]);

  // Zoom out to 75% when viewport width is 1024–1366px (iPad landscape range)
  useEffect(() => {
    const check = () => {
      const w = window.innerWidth;
      setZoomOutForIpad(w >= IPAD_LANDSCAPE_WIDTH_MIN && w <= IPAD_LANDSCAPE_WIDTH_MAX);
    };
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  // ========================================================================
  // Client-side stock filter (OR between 店舗 / 倉庫・仕入先)
  // ========================================================================
  const filteredTireList = useMemo(() => {
    if (!stockFilterStore && !stockFilterDepotVendor) return tireList;
    return tireList.filter((tire) => {
      const storeMatch = stockFilterStore && (tire.store_quantity ?? 0) > 0;
      const depotVendorMatch =
        stockFilterDepotVendor &&
        ((tire.depot_quantity ?? 0) > 0 || (tire.vendor_centor_quantity ?? 0) > 0);
      return storeMatch || depotVendorMatch;
    });
  }, [tireList, stockFilterStore, stockFilterDepotVendor]);

  // ========================================================================
  // Render
  // ========================================================================
  const pageContent = (
    <>
      <Box sx={{ px: { xs: 2, md: 2.5, lg: 5 }, pt: { xs: 2, md: 2.5, lg: 3 } }}>
        <Button onClick={() => navigate(-1)} variant="custom-outlined">
          <Box>＜ 戻る</Box>
        </Button>
      </Box>
      <Box sx={{ p: { xs: 2, md: 2.5, lg: 5 } }}>
        <Box
          sx={{
            display: "flex",
            flexWrap: { xs: "wrap", md: "nowrap" },
            justifyContent: "space-between",
            marginBottom: "20px",
            columnGap: "20px",
            rowGap: "10px",
          }}
        >
          {/* Left Section - Tire Info */}
          <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "space-between", gap: { xs: 1, md: 3 }, width: { xs: "100%", xl: "calc((100% - 80px) *3 / 5)" } }}>
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: 28, md: 31, lg: 33 },
                fontWeight: "medium",
                color: "var(--gray-700)",
                mb: { xs: "20px" },
              }}
            >
              タイヤを選ぶ
            </Typography>

            <TireInfoSection
              carImage={prevSite === "maker" ? maker.car_image_url : undefined}
              carModel={renderSelectedCar}
              tireSize={
                maker?.selectedTire?.mmm_car_tiresize || carTireListByCode[0]?.tire_size || ""
              }
              tireType={tireCategoryText}
              sizeSpecification={maker?.selectedTire?.mmm_inch}
            />
          </Box>

          {/* Right Section - Proc Fee Table */}
          <ProcFeeTable
            procFees={procFees}
            isDisposalChecked={isDisposalChecked}
            isValveChecked={isValveChecked}
            isNitrogenChecked={isNitrogenChecked}
            isHubProtectionChecked={isHubProtectionChecked}
            punctureCompensation={punctureCompensation}
            cashbackPeriod={cashbackPeriod}
            tireNumber={tireNumber}
            errors={errors}
            onDisposalChange={handleDisposalChange}
            onValveChange={handleValveChange}
            onNitrogenChange={handleNitrogenChange}
            onHubProtectionChange={handleHubProtectionChange}
            onPunctureCompensationChange={setPunctureCompensation}
            onCashbackPeriodChange={setCashbackPeriod}
            onChangeInput={onChangeInput}
            onError={handleErrorChange}
          />
        </Box>
      </Box>

      {/* Tire List Section */}
      <Box
        sx={{
          backgroundColor: "var(--offwhite-100)",
          p: { xs: 2, md: 2.5, lg: 5 },
          pt: { xl: "24px !important" },
        }}
      >
        {/* Action Buttons */}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            rowGap: "20px",
            justifyContent: "space-between",
            mb: 2,
          }}
        >
          <Box sx={{ display: "flex", flexWrap: "wrap", columnGap: "9px", rowGap: "20px" }}>
            <ExportButton
              label="選択した商品にホイールを組み合わせる"
              onClick={navigateConfirmResult}
              sx={{
                whiteSpace: "nowrap",
                width: "max-content",
                fontSize: { xs: 16, md: 20, lg: 23 },
              }}
            />
          </Box>
          <ExportButton
            label="絞り込み"
            onClick={handleFilterClick}
            sx={{ fontSize: { xs: 16, md: 20, lg: 23 } }}
          />
        </Box>

        {/* Tire List Table */}
        <TireListTable
          tireList={filteredTireList}
          tireNumber={tireNumber}
          selectedTireIds={selectedTireIds}
          cashbackPeriod={cashbackPeriod}
          procFees={procFees}
          errors={errors}
          calculator={calculator}
          onSelectTire={onSelectTire}
          onTireListChange={setTireList}
          onErrorChange={handleErrorChange}
        />

        {renderNodeLoader()}
      </Box>

      {/* Filter Modal */}
      {isModalOpen && (
        <FilterProcFeeModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          makerList={makerList}
          selectedMaker={selectedMaker}
          setSelectedMaker={setSelectedMaker}
          categoryList={categoryList}
          selectedCategory={selectedCategory}
          setSelectedCategory={setSelectedCategory}
          recommendFlag={recommendFlag}
          setRecommendFlag={setRecommendFlag}
          stockFilterStore={stockFilterStore}
          stockFilterDepotVendor={stockFilterDepotVendor}
          handleFilter={async (values) => {
            if (values) {
              setStockFilterStore(values.stockFilterStore ?? false);
              setStockFilterDepotVendor(values.stockFilterDepotVendor ?? false);
            }
            await handleFilter(values);
            setIsModalOpen(false);
          }}
        />
      )}
    </>
  );

  // iPad landscape (1024–1366px): zoom out entire page to 75%
  if (zoomOutForIpad) {
    return (
      <Box sx={{ width: "100%", overflow: "hidden" }}>
        <Box
          sx={{
            width: `${100 / ZOOM_OUT_SCALE}%`,
            minHeight: `${100 / ZOOM_OUT_SCALE}%`,
            transform: `scale(${ZOOM_OUT_SCALE})`,
            transformOrigin: "top left",
          }}
        >
          {pageContent}
        </Box>
      </Box>
    );
  }
  return pageContent;
}

export default SetTireProcFeeList;
